import * as THREE from 'three';
import { scene } from './scene';
/* ============================================================
   特效池：billboard 光點 + 定向面片
   ============================================================

   兩種原語就夠鋪滿整套武器特效：

   - **sprite**（`THREE.Sprite`）永遠面向鏡頭。光暈、十字閃光、火花、煙都是它。
     非等比縮放 + `material.rotation` 就能當「有方向的火花」用。
   - **quad**（貼在 XZ 平面或任意朝向的面片）給「有固定方向」的東西：
     地面衝擊環、斬擊弧、燒灼痕。這些一旦 billboard 就會穿地。

   ## 為什麼每個實例自己一份材質

   透明度與顏色是逐粒子的。共用材質就只能整批同色同透明度，
   於是每一種特效都要自己一個池子 —— 那才是真的浪費。
   材質只在開機時建一次（220 + 72 個），之後零配置。

   ## 為什麼沒有動態點光源

   爆炸打一盞 PointLight 是最直覺的作法，但 three 的光源數量會進 shader 定義：
   場上 300 隻敵人 × 每個 fragment 多算兩盞點光 = 純虧的 fragment 成本，
   而且新增／移除光源會觸發 shader 重編（畫面會卡一下）。
   這裡改用「很亮的加法光暈 + 既有的泛光門檻 0.66」來表達爆光 ——
   `render/postfx.ts` 的 UnrealBloom 會替我們把它糊開，成本是固定的一張全螢幕 pass。

   ## headless

   `document` 不存在時 `vfx-tex.ts` 的貼圖回 `null`，這裡的 `READY` 就是 false，
   所有 spawn 直接 return。模擬層不需要知道有沒有畫面。 */

/* 池子大小 = **同屏 draw call 的上限**。sprite 與 quad 各是一個 draw call
   （r128 的 Sprite 沒有實例化路徑），所以這兩個數字是效能預算不是記憶體預算：
   基準線是 624 個 draw call，這裡最多再加 204。
   實際同時活著的量另外由 `render/weapon-fx.ts` 的每幀配額壓住（約 30–60）。 */
var SPRITES = 140, QUADS = 64;

type Part = {
  m: any; mat: any;
  life: number; ttl: number;
  s0: number; s1: number; ar: number;
  op: number; pow: number; ease: number;
  v: THREE.Vector3; drag: number; grav: number;
  spin: number; drawn: number;
  /** quad 專用：自轉軸（繞自己的法線轉）。 */
  quad: boolean;
};

var sp: Part[] = [], qd: Part[] = [];
var READY = false;
var PLANE: any = null;

/** 特效的 renderOrder。比地面高、比玩家機體低（機體是不透明的實體，
    靠深度測試自然遮擋；這裡的排序只決定同深度的透明物件誰先畫）。 */
export var VFX_ORDER = 6;

export function initVfxPool() {
  if (READY) return true;
  if (typeof document === 'undefined') return false;
  PLANE = new THREE.PlaneGeometry(1, 1);
  var i: number;
  for (i = 0; i < SPRITES; i++) {
    var mt = new THREE.SpriteMaterial({
      color: 0xffffff, transparent: true, opacity: 0,
      blending: THREE.AdditiveBlending, depthWrite: false, depthTest: true
    });
    var s = new THREE.Sprite(mt);
    s.visible = false; s.renderOrder = VFX_ORDER; s.frustumCulled = false;
    scene.add(s);
    sp.push(mkPart(s, mt, false));
  }
  for (i = 0; i < QUADS; i++) {
    var qm = new THREE.MeshBasicMaterial({
      color: 0xffffff, transparent: true, opacity: 0, side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending, depthWrite: false
    });
    var q = new THREE.Mesh(PLANE, qm);
    q.visible = false; q.renderOrder = VFX_ORDER; q.frustumCulled = false;
    scene.add(q);
    qd.push(mkPart(q, qm, true));
  }
  READY = true;
  return true;
}

function mkPart(m: any, mat: any, quad: boolean): Part {
  return {
    m: m, mat: mat, life: 0, ttl: 1, s0: 1, s1: 1, ar: 1,
    op: 1, pow: 1.6, ease: 2.2, v: new THREE.Vector3(), drag: 0, grav: 0,
    spin: 0, drawn: 0, quad: quad
  };
}

var ENABLED = true;
export function vfxReady() { return READY && ENABLED; }
/** 特效總開關。存在的理由是**量測**：要說「特效讓 draw call 多了幾個」，
    唯一誠實的作法是同一份 build 開關兩次量（不同 build 之間還混著別的改動）。
    順帶當成之後畫質設定的掛勾。 */
export function vfxEnable(on: boolean) {
  ENABLED = on;
  if (on) return ENABLED;
  for (var a = 0; a < 2; a++) {
    var arr = a ? qd : sp;
    for (var i = 0; i < arr.length; i++) {
      arr[i].life = 0; arr[i].drawn = 0;
      arr[i].m.visible = false; arr[i].mat.opacity = 0;
    }
  }
  return ENABLED;
}
/** 給效能面板／診斷用：目前有幾個粒子活著。 */
export function vfxAlive() {
  var n = 0, i;
  for (i = 0; i < sp.length; i++) if (sp[i].life > 0) n++;
  for (i = 0; i < qd.length; i++) if (qd[i].life > 0) n++;
  return n;
}

/** 診斷用：回報第一顆活著的粒子的實際狀態。
    「截圖上看不到」有五種可能（沒生成／位置錯／尺寸 0／透明度 0／貼圖沒吃到），
    用眼睛分不出來，只能把數字印出來。 */
export function vfxProbe() {
  for (var a = 0; a < 2; a++) {
    var arr = a ? qd : sp;
    for (var i = 0; i < arr.length; i++) {
      var p = arr[i];
      if (p.life <= 0) continue;
      return {
        kind: a ? 'quad' : 'sprite', life: +p.life.toFixed(3), ttl: p.ttl,
        op: +p.mat.opacity.toFixed(3), vis: p.m.visible,
        map: !!p.mat.map, blend: p.mat.blending,
        pos: [+p.m.position.x.toFixed(1), +p.m.position.y.toFixed(1), +p.m.position.z.toFixed(1)],
        scl: [+p.m.scale.x.toFixed(2), +p.m.scale.y.toFixed(2)],
        col: p.mat.color.getHexString()
      };
    }
  }
  return null;
}

function take(arr: Part[]) {
  for (var i = 0; i < arr.length; i++) if (arr[i].life <= 0) return arr[i];
  return null;   // 池滿就不生 —— 特效寧可少一個，不要為它配置記憶體
}

export type SpriteOpt = {
  /** 顏色（hex）。加法混色下這個值就是亮度，暖色要壓一點才不會整片糊成白。 */
  c?: number;
  /** 起始／結束尺寸。`s1` 省略＝不變。 */
  s?: number; s1?: number;
  /** y/x 比。做「拉長的火花」用（配 `rot` 指定方向）。 */
  ar?: number;
  /** 壽命（秒）。 */
  t?: number;
  /** 峰值不透明度。**玩家武器特效的上限是 0.75**（見 06 手感規格）。 */
  op?: number;
  /** 淡出次方。愈大愈「一閃即逝」。 */
  pow?: number;
  /** 尺寸曲線次方：>1 = 先快後慢（爆開），1 = 線性。 */
  ease?: number;
  /** 初速與阻力／重力。 */
  vx?: number; vy?: number; vz?: number; drag?: number; grav?: number;
  /** 平面內旋轉（弧度）與自轉速度。 */
  rot?: number; spin?: number;
};

/** 生一顆 billboard 光點。`tex` 為 null（headless）時什麼都不做。

    `normal: true` 走**一般混色**（不是加法）。煙與塵一定要走這條 ——
    加法混色只會把背後的東西愈疊愈亮，而煙的重點是**它擋住了背後的東西**，
    那是「這團有實體」唯一的線索（2026-08-05：使用者要實彈感不要魔法感）。 */
export function spawnSprite(tex: any, x: number, y: number, z: number, o?: SpriteOpt & { normal?: boolean }) {
  if (!READY || !tex) return null;
  var p = take(sp); if (!p) return null;
  o = o || {};
  p.mat.map = tex; p.mat.color.setHex(o.c === undefined ? 0xffffff : o.c);
  p.mat.rotation = o.rot || 0;
  p.mat.blending = o.normal ? THREE.NormalBlending : THREE.AdditiveBlending;
  p.mat.opacity = 0; p.mat.needsUpdate = true;
  p.s0 = o.s === undefined ? 1 : o.s;
  p.s1 = o.s1 === undefined ? p.s0 : o.s1;
  p.ar = o.ar === undefined ? 1 : o.ar;
  p.ttl = o.t === undefined ? 0.3 : o.t; p.life = p.ttl;
  p.op = o.op === undefined ? 1 : o.op;
  p.pow = o.pow === undefined ? 1.6 : o.pow;
  p.ease = o.ease === undefined ? 2.2 : o.ease;
  p.v.set(o.vx || 0, o.vy || 0, o.vz || 0);
  p.drag = o.drag === undefined ? 2.5 : o.drag;
  p.grav = o.grav || 0;
  p.spin = o.spin || 0; p.drawn = 0;
  p.m.position.set(x, y, z);
  p.m.scale.set(p.s0, p.s0 * p.ar, 1);
  p.m.visible = true;
  return p;
}

/** 生一片定向面片。`rx/ry/rz` 是尤拉角；躺平在地上就給 `-Math.PI/2, yaw, 0`。 */
export function spawnQuad(tex: any, x: number, y: number, z: number,
  rx: number, ry: number, rz: number, o?: SpriteOpt & { normal?: boolean }) {
  if (!READY || !tex) return null;
  var p = take(qd); if (!p) return null;
  o = o || {};
  p.mat.map = tex; p.mat.color.setHex(o.c === undefined ? 0xffffff : o.c);
  /* `normal: true` 走一般混色 —— 燒灼痕那種「把地面壓暗」的東西
     用加法混色只會愈畫愈亮，方向剛好相反。 */
  p.mat.blending = o.normal ? THREE.NormalBlending : THREE.AdditiveBlending;
  p.mat.opacity = 0; p.mat.needsUpdate = true;
  p.s0 = o.s === undefined ? 1 : o.s;
  p.s1 = o.s1 === undefined ? p.s0 : o.s1;
  p.ar = o.ar === undefined ? 1 : o.ar;
  p.ttl = o.t === undefined ? 0.3 : o.t; p.life = p.ttl;
  p.op = o.op === undefined ? 1 : o.op;
  p.pow = o.pow === undefined ? 1.6 : o.pow;
  p.ease = o.ease === undefined ? 2.2 : o.ease;
  p.v.set(o.vx || 0, o.vy || 0, o.vz || 0);
  p.drag = o.drag === undefined ? 2.5 : o.drag;
  p.grav = o.grav || 0;
  p.spin = o.spin || 0; p.drawn = 0;
  p.m.position.set(x, y, z);
  /* **`YXZ` 不是隨手選的。** 預設的 `XYZ` 會先繞 Y 再繞 X，於是「躺平之後
     再轉方向」變成「轉方向之後再躺平」—— 面片會歪出地平面。
     `YXZ` 的矩陣是 Ry·Rx·Rz，等同既有 `slashFx` 那種「父群組轉向、
     子物件躺平」的組合，只是省掉一層 Group。 */
  p.m.rotation.set(rx, ry, rz, 'YXZ');
  p.m.scale.set(p.s0, p.s0 * p.ar, 1);
  p.m.visible = true;
  return p;
}

/* **每顆粒子至少要被畫兩幀。**

   槍口閃光的壽命是 0.08 秒 —— 60 fps 下是 5 幀，很夠；但幀時間一拉長
   （低階機器、軟體渲染的截圖環境、分頁剛切回來）dt 就會大於壽命，
   於是粒子在「第一次更新」就死了，**一幀都沒被畫到**。
   症狀非常誤導：診斷讀數說特效有生成、位置與尺寸都對，但畫面上什麼都沒有。
   （這一輪就是這樣繞了一圈才找到。）

   所以壽命歸零時先看畫過幾幀：不足就強制再留一幀，並把淡出曲線壓到中段，
   讓那一幀是「看得到的殘影」而不是「透明度 0 的空畫」。 */
var MIN_FRAMES = 2;

function step(arr: Part[], dt: number) {
  for (var i = 0; i < arr.length; i++) {
    var p = arr[i];
    if (p.life <= 0) continue;
    p.life -= dt;
    var k = 1 - p.life / p.ttl;                       // 0 → 1
    if (p.life <= 0) {
      if (p.drawn >= MIN_FRAMES) { p.m.visible = false; p.mat.opacity = 0; p.drawn = 0; continue; }
      p.life = 1e-6; k = p.drawn ? 0.55 : 0;
    }
    p.drawn++;
    if (p.drag) p.v.multiplyScalar(Math.max(0, 1 - p.drag * dt));
    if (p.grav) p.v.y -= p.grav * dt;
    if (p.v.x || p.v.y || p.v.z) p.m.position.addScaledVector(p.v, dt);
    var ke = 1 - Math.pow(1 - k, p.ease);             // 先快後慢
    var s = p.s0 + (p.s1 - p.s0) * ke;
    p.m.scale.set(s, s * p.ar, 1);
    if (p.spin) {
      if (p.quad) p.m.rotateZ(p.spin * dt); else p.mat.rotation += p.spin * dt;
    }
    p.mat.opacity = p.op * Math.pow(1 - k, p.pow);
  }
}

/** 每畫面幀呼叫一次（吃 `STATE.timeScale`，所以 hitstop 時特效也會慢下來）。 */
export function updateVfxPool(dt: number) {
  if (!READY || dt <= 0) return;
  step(sp, dt); step(qd, dt);
}
